import { memo, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { V2QsSelectProps, V2QsSelectValue, V2QsSelectOption } from './types';

/**
 * V2QsSelect 컴포넌트
 *
 * Radio(단일 선택) 또는 Checkbox(다중 선택)을 위한 컴포넌트
 * V1 QsInputSelectorV2의 V2 버전으로 순수 UI만 담당
 *
 * @example
 * ```tsx
 * // Radio 모드 (단일 선택)
 * <V2QsSelect
 *   mode="radio"
 *   options={[
 *     { key: 'yes', label: 'Yes' },
 *     { key: 'no', label: 'No' },
 *   ]}
 *   value={{ selectedKeys: ['yes'], notesForKeys: {} }}
 *   onChange={setValue}
 * />
 *
 * // Checkbox 모드 (다중 선택) with requireNote
 * <V2QsSelect
 *   mode="checkbox"
 *   options={[
 *     { key: 'option1', label: 'Option 1' },
 *     { key: 'other', label: 'Other', requireNote: true },
 *   ]}
 *   value={{ selectedKeys: ['other'], notesForKeys: { other: 'Custom input' } }}
 *   onChange={setValue}
 * />
 * ```
 */
function V2QsSelect({
  options,
  value,
  onChange,
  mode = 'checkbox',
  error = false,
  errorMessage,
  disabled = false,
  fullWidth = true,
  className,
  containerClassName,
  label,
  hint,
  notePlaceholder = 'Please describe in detail...',
  spacing = 'normal',
}: V2QsSelectProps) {
  const { selectedKeys = [], notesForKeys = {} } = value || {};

  // mutual exclusive 옵션 키 목록
  const mutualExclusiveKeys = useMemo(
    () => options.filter((opt) => opt.mutualExclusive).map((opt) => opt.key),
    [options]
  );

  // 체크박스 변경 핸들러
  const handleCheckboxChange = useCallback(
    (option: V2QsSelectOption, checked: boolean) => {
      if (!onChange) return;

      let newSelectedKeys: string[];
      let newNotesForKeys = { ...notesForKeys };

      if (checked) {
        if (option.mutualExclusive) {
          // mutual exclusive 옵션 선택 시 다른 옵션 모두 해제
          newSelectedKeys = [option.key];
          // 다른 옵션의 노트도 삭제
          newNotesForKeys = option.requireNote && notesForKeys[option.key]
            ? { [option.key]: notesForKeys[option.key] }
            : {};
        } else {
          // 일반 옵션 선택 시 mutual exclusive 옵션 해제
          newSelectedKeys = [...selectedKeys.filter((k) => !mutualExclusiveKeys.includes(k)), option.key];
          // mutual exclusive 옵션의 노트 삭제
          mutualExclusiveKeys.forEach((k) => {
            delete newNotesForKeys[k];
          });
        }
      } else {
        // 선택 해제
        newSelectedKeys = selectedKeys.filter((k) => k !== option.key);
        // 해제된 옵션의 노트 삭제
        delete newNotesForKeys[option.key];
      }

      onChange({
        selectedKeys: newSelectedKeys,
        notesForKeys: newNotesForKeys,
      });
    },
    [onChange, selectedKeys, notesForKeys, mutualExclusiveKeys]
  );

  // 라디오 변경 핸들러
  const handleRadioChange = useCallback(
    (option: V2QsSelectOption) => {
      if (!onChange) return;

      // 이전 선택의 노트 삭제하고 새 선택으로 교체
      const newNotesForKeys: Record<string, string> = {};
      if (option.requireNote && notesForKeys[option.key]) {
        newNotesForKeys[option.key] = notesForKeys[option.key];
      }

      onChange({
        selectedKeys: [option.key],
        notesForKeys: newNotesForKeys,
      });
    },
    [onChange, notesForKeys]
  );

  // 노트 변경 핸들러
  const handleNoteChange = useCallback(
    (optionKey: string, noteValue: string) => {
      if (!onChange) return;

      onChange({
        selectedKeys,
        notesForKeys: {
          ...notesForKeys,
          [optionKey]: noteValue,
        },
      });
    },
    [onChange, selectedKeys, notesForKeys]
  );

  const hasError = error;

  // Container 클래스
  const containerClasses = classNames('flex flex-col gap-1', containerClassName);

  // 옵션 목록 클래스
  const optionsClasses = classNames(
    'flex flex-col',
    {
      'gap-1': spacing === 'compact',
      'gap-2': spacing === 'normal',
      'gap-3': spacing === 'relaxed',
      'w-full': fullWidth,
    },
    className
  );

  // 옵션 아이템 클래스
  const getOptionClasses = (isSelected: boolean, isDisabled: boolean) =>
    classNames(
      'flex flex-col gap-3 cursor-pointer transition-all duration-200',
      'border-[1.5px] p-3 rounded-[10px]',
      {
        // Border color
        'border-[#4B7BF5]': isSelected && !isDisabled,
        'border-[#E6E9F5]': !isSelected && !isDisabled,
        'border-[#D0D4E4]': isDisabled, // disabled 상태에서 테두리 더 진하게

        // Background
        'bg-white': !isDisabled,
        'bg-[#F8F9FC]': isDisabled, // disabled 배경색 약간 더 밝게

        // Hover
        'hover:border-[#DADDEB]': !isSelected && !isDisabled,

        // Cursor
        'cursor-default': isDisabled,
      }
    );

  // 라벨 클래스
  const labelClasses = 'text-sm font-medium text-[#222638] mb-1';

  // 힌트 클래스
  const hintClasses = 'text-xs text-[#AFB2C2]';

  // 에러 메시지 클래스
  const errorClasses = 'text-xs text-[#FF5E71]';

  // 입력 공통 클래스 (checkbox/radio) - V1과 동일하게 항상 primary 색상 적용
  const inputClasses = (isSelected: boolean, isDisabled: boolean) =>
    classNames(
      'shrink-0',
      {
        'checkbox checkbox-lg checkbox-primary': mode === 'checkbox',
        'radio radio-lg radio-primary': mode === 'radio',
        // disabled 상태에서도 테두리가 잘 보이도록 (더 진한 테두리)
        '!border-[2.5px] !border-[#9CA3B8]': isDisabled,
      }
    );

  // 노트 입력 필드 클래스
  const noteInputClasses = classNames(
    'input w-full transition-all duration-200 rounded-[10px]',
    'text-[14px] leading-[1.6] px-4 py-3',
    'border-[1.5px] border-[#E6E9F5]',
    'focus:border-[#4B7BF5] focus:outline-none',
    'placeholder:text-[#AFB2C2]'
  );

  // 옵션 렌더링
  const renderOption = (option: V2QsSelectOption) => {
    const isSelected = selectedKeys.includes(option.key);
    const isDisabled = disabled || !!option.disabled;
    const showNoteInput = isSelected && option.requireNote;
    const noteValue = notesForKeys[option.key] || '';

    return (
      <label
        key={option.key}
        className={getOptionClasses(isSelected, isDisabled)}
      >
        <div className="flex-1 w-full flex flex-row gap-4 items-start">
          <input
            type={mode === 'radio' ? 'radio' : 'checkbox'}
            checked={isSelected}
            disabled={isDisabled}
            className={inputClasses(isSelected, isDisabled)}
            onChange={(e) => {
              if (isDisabled) return;
              if (mode === 'radio') {
                handleRadioChange(option);
              } else {
                handleCheckboxChange(option, e.target.checked);
              }
            }}
          />
          <span className="pt-[0.2rem] text-[15px] text-[#222638]">
            {option.label}
          </span>
        </div>

        {/* Note Input */}
        {showNoteInput && (
          <div className="w-full" onClick={(e) => e.stopPropagation()}>
            <input
              type="text"
              value={noteValue}
              placeholder={notePlaceholder}
              disabled={isDisabled}
              className={noteInputClasses}
              onChange={(e) => handleNoteChange(option.key, e.target.value)}
            />
          </div>
        )}
      </label>
    );
  };

  return (
    <div className={containerClasses}>
      {/* Label */}
      {label && <label className={labelClasses}>{label}</label>}

      {/* Options */}
      <div className={optionsClasses}>
        {options.map(renderOption)}
      </div>

      {/* Hint */}
      {hint && !hasError && <span className={hintClasses}>{hint}</span>}

      {/* Error Message */}
      {hasError && errorMessage && (
        <span className={errorClasses}>{errorMessage}</span>
      )}
    </div>
  );
}

export default memo(V2QsSelect);
